import React, { useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Flex } from 'antd';
import { ResponsiveLine } from '@nivo/line';
import { FaCaretDown, FaCaretRight, FaCaretUp } from 'react-icons/fa';
import useOccurrences from 'src/pages/dashboard/contexts/useOccurrences';
import { WidgetSubtitle, WidgetTitle } from 'src/pages/dashboard/styles';

interface IDataResult {
	[key: string]: { id: string, color: string, data: { x: string, y: number }[], isRecovery?: boolean }
}

export default function OccurrencesByPeriod() {
	const { occurrences, lastPeriodOccurrences, period, } = useOccurrences();

	const datesInterval = useMemo(() => {
		const firstDate = dayjs(period.inf_date);
		const lastDate = dayjs(period.sup_date);

		const allInterval = [];
		let currentDate = firstDate;
		while (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate, 'day')) {
			allInterval.push(currentDate.format('DD/MM/YY'));
			currentDate = currentDate.add(1, 'day');
		}

		return allInterval;
	}, [ period, ]);

	const data = useMemo(() => {
		const alertOccurrences = occurrences.filter(occ => !occ.is_recovery);
		const recoveryOccurrences = occurrences.filter(occ => occ.is_recovery);

		const grouped = _.groupBy(alertOccurrences, occ => {
			const dateTime = dayjs(occ.creation_date).format('DD/MM/YY');
			const severityId = occ.observer.severity.severity_id;
			return `${dateTime}::${severityId}`;
		});

		const severityMap = _.reduce(grouped, (result: IDataResult, group, key) => {
			const [ creation_date, severity_id, ] = key.split('::');
			const severity = group[0].observer.severity;

			if (!result[severity_id]) {
				result[severity_id] = {
					id: severity.name,
					color: severity.color,
					data: [],
				};
			}

			result[severity_id].data.push({
				x: creation_date,
				y: group.length,
			});

			return result;
		}, {});

		const recoveryData = {
			id: 'Recuperadas',
			color: '#52c41a',
			data: datesInterval.map(date => {
				const count = recoveryOccurrences.filter(occ =>
					dayjs(occ.creation_date).format('DD/MM/YY') === date
				).length;
				return { x: date, y: count, };
			}),
			isRecovery: true,
		};

		const filledData = Object.values(severityMap).map(severity => {
			const newData = datesInterval.map(date => {
				const existingDataPoint = severity.data.find(dataPoint => 
					dataPoint.x === date
				);
				if (existingDataPoint) return existingDataPoint;
				return { x: date, y: 0, };
			});
			return { ...severity, data: newData, };
		});

		return [ ...filledData, recoveryData, ];
	}, [ occurrences, datesInterval, ]);

	const statistic = useMemo(() => {
		const currentTotal = occurrences.filter(occ => !occ.is_recovery).length;

		const lastPeriodTotal = lastPeriodOccurrences
			.filter(occ => !occ.is_recovery)
			.length;

		if (lastPeriodTotal === 0) return 100;

		return ((currentTotal - lastPeriodTotal) / lastPeriodTotal) * 100;
	}, [ lastPeriodOccurrences.length, occurrences.length, ]);

	return (
		<Flex vertical style={{ height: '100%', }}>
			<Flex vertical gap={4}>
				<WidgetTitle>Ocorrências por período</WidgetTitle>
				<WidgetSubtitle>
					<WidgetSubtitle
						style={{
							color: statistic > 0 ? '#FF0000' : statistic < 0 ? '#52C41A' : 'inherit',
							borderColor: statistic > 0 ? '#FF0000' : statistic < 0 ? '#52C41A' : 'inherit',
						}}
					>
						{statistic > 0 ? <FaCaretUp /> : statistic < 0 ? <FaCaretDown /> : <FaCaretRight />}
						{Math.abs(statistic)?.toFixed(2) + '%'}
					</WidgetSubtitle>
					{' vs. periodo anterior'}
				</WidgetSubtitle>
			</Flex>
			<ResponsiveLine
				data={data}
				curve="monotoneX"
				margin={{ top: 50, right: 50, bottom: 80, left: 60, }}
				axisLeft={{
					tickSize: 5,
					tickPadding: 5,
					tickRotation: 0,
					legend: 'Ocorrências',
					legendOffset: -40,
					legendPosition: 'middle',
					truncateTickAt: 0,
				}}
				animate={false}
				enableGridX={false}
				colors={{ datum: 'color', }}
				lineWidth={4}
				enablePoints={false}
				useMesh={false}
				enableSlices="x"
				legends={[
					{
						anchor: 'top',
						direction: 'row',
						justify: false,
						translateX: 0,
						translateY: -25,
						itemsSpacing: 8,
						itemDirection: 'left-to-right',
						itemWidth: 80,
						itemHeight: 10,
						itemOpacity: 0.75,
						symbolSize: 12,
						symbolShape: 'circle',
						symbolBorderColor: 'rgba(0, 0, 0, .5)',
						effects: [
							{
								on: 'hover',
								style: {
									itemBackground: 'rgba(0, 0, 0, .03)',
									itemOpacity: 1,
								},
							},
						],
					},
				]}
				theme={{
					// lines: {
					// 	strokeDasharray: ({ datum, }) => 
					// 		datum.isRecovery ? '4 4' : 'none',
					// },
				}}
			/>
		</Flex>
	);
}
